import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/auth";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import logo from "../../components/images/logo-transparent-png.png";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import CategoryIcon from "@mui/icons-material/Category";
import ImageIcon from "@mui/icons-material/Image";
import EmailIcon from "@mui/icons-material/Email";
import BarChartIcon from "@mui/icons-material/BarChart";
import LogoutIcon from "@mui/icons-material/Logout";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [auth, setAuth] = useAuth();
  const [currentQuote, setCurrentQuote] = useState({ text: "", author: "" });

  // Array of motivational quotes
  const quotes = [
    { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
    { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
    { text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
    { text: "The future depends on what you do today.", author: "Mahatma Gandhi" },
    { text: "Success is walking from failure to failure with no loss of enthusiasm.", author: "Winston Churchill" },
    { text: "Everything you've ever wanted is on the other side of fear.", author: "George Addair" },
    { text: "Opportunities don't happen. You create them.", author: "Chris Grosser" },
    { text: "The only limit to our realization of tomorrow will be our doubts of today.", author: "Franklin D. Roosevelt" },
    { text: "Do what you can, with what you have, where you are.", author: "Theodore Roosevelt" },
    { text: "It does not matter how slowly you go as long as you do not stop.", author: "Confucius" },
    { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
    { text: "Your time is limited, don't waste it living someone else's life.", author: "Steve Jobs" },
    { text: "Whether you think you can or you think you can't, you're right.", author: "Henry Ford" },
    { text: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" },
    { text: "Don't be afraid to give up the good to go for the great.", author: "John D. Rockefeller" },
    { text: "I find that the harder I work, the more luck I seem to have.", author: "Thomas Jefferson" },
    { text: "Success usually comes to those who are too busy to be looking for it.", author: "Henry David Thoreau" },
    { text: "Don't let yesterday take up too much of today.", author: "Will Rogers" },
    { text: "You learn more from failure than from success. Don't let it stop you.", author: "Unknown" },
    { text: "It's not whether you get knocked down, it's whether you get up.", author: "Vince Lombardi" },
    { text: "If you are working on something that you really care about, you don't have to be pushed.", author: "Steve Jobs" },
    { text: "People who are crazy enough to think they can change the world, are the ones who do.", author: "Rob Siltanen" },
    { text: "Failure will never overtake me if my determination to succeed is strong enough.", author: "Og Mandino" },
    { text: "We may encounter many defeats but we must not be defeated.", author: "Maya Angelou" },
    { text: "Knowing is not enough; we must apply. Wishing is not enough; we must do.", author: "Johann Wolfgang Von Goethe" },
    { text: "Imagine your life is perfect in every respect; what would it look like?", author: "Brian Tracy" },
    { text: "We generate fears while we sit. We overcome them by action.", author: "Dr. Henry Link" },
    { text: "Whether you think you can or think you can't, you're right.", author: "Henry Ford" },
    { text: "What you get by achieving your goals is not as important as what you become by achieving your goals.", author: "Zig Ziglar" },
    { text: "A goal without a plan is just a wish.", author: "Antoine de Saint-Exupéry" }
  ];

  // Get quote based on the day of the year
  useEffect(() => {
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
    const quoteIndex = dayOfYear % quotes.length;
    setCurrentQuote(quotes[quoteIndex]);
  }, []);

  const logout = () => {
    setAuth({ ...auth, user: null, token: "" });
    localStorage.clear();
    navigate("/");
    toast.success("Logged out successfully! See you soon!");
  };

  // Dashboard cards configuration
  const dashboardCards = [
    {
      title: "New Orders",
      icon: <ShoppingBagIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/orders",
      color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      description: "Process new orders"
    },
    {
      title: "Past Orders",
      icon: <HistoryIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/past-orders",
      color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      description: "View order history"
    },
    {
      title: "Analytics",
      icon: <BarChartIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/analytics",
      color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      description: "View insights"
    },
    {
      title: "Sale Management",
      icon: <LocalOfferIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/sales",
      color: "linear-gradient(135deg, #fdeb71 0%, #f8d800 100%)",
      description: "Manage sales & discounts"
    },
    {
      title: "Add Product",
      icon: <AddCircleIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/add",
      color: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      description: "Add new products"
    },
    {
      title: "View/Edit Products",
      icon: <EditIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/product/view",
      color: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      description: "Manage inventory"
    },
    {
      title: "Add Subcategory",
      icon: <AddCircleIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/subcategory/add",
      color: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
      description: "Create categories"
    },
    {
      title: "View/Edit Categories",
      icon: <CategoryIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/subcategory/view",
      color: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      description: "Manage categories"
    },
    {
      title: "Carousel Images",
      icon: <ImageIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/carousel-images",
      color: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
      description: "Update homepage"
    },
    {
      title: "Send Email",
      icon: <EmailIcon style={{ fontSize: "3rem" }} />,
      path: "/admin/send-email",
      color: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
      description: "Email customers"
    }
  ];

  return (
      <>
        <div className="admin-dashboard">
          {/* Header Section */}
          <div className="dashboard-header text-center py-4">
            <img
                src={logo}
                alt="Logo"
                className="dashboard-logo mb-3"
                style={{ width: "250px", height: "100px", objectFit: "contain" }}
            />
          </div>

          <div className="container py-4">
            {/* Welcome Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="welcome-card card shadow-lg border-0">
                  <div className="card-body p-4">
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <h2 className="mb-2" style={{ color: "#002E6A", fontWeight: "700" }}>
                          Welcome back, Kanchan! 👋
                        </h2>
                        <p className="text-muted mb-0">
                          {new Date().toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <div className="col-md-4 text-md-end mt-3 mt-md-0">
                        <button onClick={logout} className="btn btn-outline-danger btn-lg">
                          <LogoutIcon className="me-2" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Quote Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="quote-card card shadow border-0" style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white"
                }}>
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start">
                      <FormatQuoteIcon style={{ fontSize: "3rem", opacity: 0.3, marginRight: "15px" }} />
                      <div>
                        <h5 className="mb-3" style={{ fontStyle: "italic", fontWeight: "400", lineHeight: "1.6" }}>
                          "{currentQuote.text}"
                        </h5>
                        <p className="mb-0 text-end" style={{ fontSize: "1rem", fontWeight: "600" }}>
                          — {currentQuote.author}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Dashboard Cards */}
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
              {dashboardCards.map((card, index) => (
                  <div className="col" key={index}>
                    <Link to={card.path} className="text-decoration-none">
                      <div
                          className="dashboard-card card h-100 border-0 shadow-sm"
                          style={{
                            background: card.color,
                            color: "white",
                            transition: "all 0.3s ease"
                          }}
                      >
                        <div className="card-body d-flex flex-column justify-content-center align-items-center text-center p-4">
                          <div className="icon-wrapper mb-3">
                            {card.icon}
                          </div>
                          <h4 className="card-title mb-2" style={{ fontWeight: "600" }}>
                            {card.title}
                          </h4>
                          <p className="card-text mb-0" style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                            {card.description}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>
              ))}
            </div>
          </div>
        </div>

        <style jsx>{`
        .admin-dashboard {
          min-height: 100vh;
          background: linear-gradient(to bottom, #f8f9fa 0%, #e9ecef 100%);
        }

        .welcome-card {
          border-radius: 15px;
          border-left: 5px solid #667eea;
        }

        .quote-card {
          border-radius: 15px;
          position: relative;
          overflow: hidden;
        }

        .quote-card::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          animation: pulse 15s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .dashboard-card {
          min-height: 200px;
          border-radius: 15px;
          cursor: pointer;
          overflow: hidden;
          position: relative;
        }

        .dashboard-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(255, 255, 255, 0.1);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .dashboard-card:hover {
          transform: translateY(-10px) scale(1.02);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2) !important;
        }

        .dashboard-card:hover::before {
          opacity: 1;
        }

        .icon-wrapper {
          background: rgba(255, 255, 255, 0.2);
          padding: 20px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .dashboard-logo {
          filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
        }

        @media (max-width: 768px) {
          .dashboard-card {
            min-height: 150px;
          }
          
          .icon-wrapper svg {
            font-size: 2rem !important;
          }
        }
      `}</style>
      </>
  );
};

export default AdminDashboardPage;